import {
  Box,
  Checkbox,
  CheckboxGroup,
  Radio,
  RadioGroup,
  Stack,
  Strong,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Field, FieldProps, Form, FormikProps, useFormikContext } from "formik";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import ContactField from "./FormComponents/ContactField";
import FormikSelectField from "./FormComponents/FormikSelectField";
import GeneralTextField from "./FormComponents/GeneralTextField";
import {
  AccountPayer,
  DocumentTypeEnum,
  FormValues,
  GasOptionEnum,
} from "./types";
import {
  formGasLineHomeUserValidationSchema,
  formGasLineWithCompanyValidationSchema,
} from "./FormComponents/Validations";
import { apiClient } from "src/api/apiService";
import Calculator from "./FormComponents/Calculator";
import FormContext from "src/context/FormContext";
import {
  hasTotalLoadDetails,
  isUnavailableDateForFuture,
  meterLocationOptions,
  populateTotalLoadSummary,
  pressureOptions,
  preSubmissionRequestUpdate,
  sendEicComms,
  showUpdatePremiseAlert,
  siteConditionsOptions,
  submitBlock,
  validateAddress,
  validatePlumberLicense,
} from "./helper";

import * as Yup from "yup";
import DatePickerField from "./FormComponents/DatePickerField";
import { useNavigate } from "react-router-dom";
import { getLodgeErrorTypeEnum } from "../../api/apiHelper";
import ResidentialGasLoadField from "./FormComponents/ResidentialGasLoadField";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import { useUpdateSidebarStatus } from "../Hooks/useSidebarStatus";
import HomeUserLicencedPlumber from "./HomeUser/HomeUserLicencedPlumber";
import HomeUserSiteSupervisor from "./HomeUser/HomeUserSiteSupervisor";
import { useAppContext } from "src/context/AppContext";
import { EICStatusEnum } from "../Premises/types";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionsActionButtons from "./PermissionsActionButtons";
import { NewPremiseFormFileUploadFormikField } from "./NewPremise/NewPremiseFormFileUploadFormikField";

interface GasLineBlockProps {
  setFormLoadValues?: (values: FormValues) => void;
  homeUser: boolean;
  premiseId: string | null;
}

const GasLineBlock: React.FC<GasLineBlockProps> = ({
  setFormLoadValues,
  homeUser,
  premiseId,
}) => {
  const {
    formState: { gasServices, isAccountReadOnly },
    setFormState,
    setDisplayingSnackbar,
    setDisplayingAlertBanner,
  } = useContext(FormContext);
  const navigate = useNavigate();
  const { values, setFieldValue, setErrors, errors } =
    useFormikContext<FormValues>();
  const [gasInputFieldsVisible, setGasInputFieldsVisible] = useState(true);
  const {
    context: { user },
  } = useAppContext();
  const { isHomeUser: isHomeUserFromPermissions } = usePermissions();
  const {
    propertyType,
    premiseAddress: { state },
    gasLine: { siteConditions, meterLocation, pressure },
  } = values;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (state === "SA") {
      setGasInputFieldsVisible(values.gasLine.buildingType === "existing");
    }
  }, [state]);

  const displaySiteSupervisor =
    values.premiseAddress?.state === "VIC" ||
    values.premiseAddress?.state === "NSW" ||
    values.premiseAddress?.state === "SA" ||
    values.premiseAddress?.state === "QLD";

  //cleaning up data from unused form pathways
  useEffect(() => {
    if (!siteConditions.includes("other")) {
      setFieldValue("gasLine.otherSiteCondition", "");
    }
  }, [siteConditions]);

  useEffect(() => {
    if (pressure !== "other" && pressure !== "0") {
      setFieldValue("gasLine.otherPressure", "");
    }
  }, [pressure]);

  useEffect(() => {
    if (meterLocation !== "other") {
      setFieldValue("gasLine.otherMeterLocation", "");
    }
  }, [meterLocation]);

  const closeDialog = () => {
    setOpen(false);
  };
  const openDialog = () => {
    setOpen(true);
  };

  const handleNewGasToggle = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) setGasInputFieldsVisible(false);
  };

  const handleExistingGasToggle = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) setGasInputFieldsVisible(true);
  };

  const isDigitalEic =
    homeUser ||
    (values.gasLine.accountPayer === AccountPayer.SOMEONE_ELSE &&
      values.accountHolderDetailsType === "online");

  const sendEicCommsOnLodge = async () => {
    if (
      isDigitalEic &&
      values.gasLine.eicStatus !== EICStatusEnum.EIC_ACCEPTED
    ) {
      const { valid, message } = await sendEicComms(
        values.id,
        gasServices?.gasLine?.requestId ?? null,
        "Notification re-sent.",
        "Account failed to save."
      );
      if (valid) {
        setDisplayingAlertBanner(null);
        navigate("/", {
          state: {
            submitted: true,
            isDigitalEic,
            snackbarMessage: message,
          },
        });
      } else {
        setDisplayingSnackbar({
          message,
          tone: valid ? "positive" : "caution",
        });
      }
    }
  };

  const lodgeRequest = async () => {
    try {
      let validAddress = false;
      if (state === "VIC") {
        const { valid } = await validateAddress({
          streetName: values.premiseAddress.streetName,
          streetType: values.premiseAddress.streetType,
          streetSuffix: values.premiseAddress.streetSuffix,
          suburb: values.premiseAddress.suburb,
          postcode: values.premiseAddress.postcode,
        });
        validAddress = valid;
      }
      const manipulatedValues =
        isHomeUserFromPermissions &&
        values.accountHolder === "person" &&
        !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
        user
          ? {
              ...values,
              accountPrimaryEmail: user.email,
              accountPrimaryFirstName: user.first_name,
              accountPrimaryLastName: user.last_name,
            }
          : values;
      await preSubmissionRequestUpdate(
        manipulatedValues,
        gasServices!,
        setFormLoadValues!,
        setFormState,
        "gasLine",
        state === "VIC"
          ? {
              [GasOptionEnum.GAS_LINE]: {
                validAddress,
              },
            }
          : {},
        undefined,
        undefined,
        isDigitalEic,
        homeUser,
        true
      );
      if (isDigitalEic) {
        await sendEicCommsOnLodge();
        return;
      }
      const { data } = await apiClient.put(
        `/submission/${gasServices?.gasLine?.requestId}`
      );
      setDisplayingAlertBanner(null);
      navigate("/", {
        state: {
          submitted: true,
          journeyId: data.services[0].journey_id,
        },
      });
    } catch (error) {
      setDisplayingAlertBanner(getLodgeErrorTypeEnum(error) ?? null);
    }
  };

  const saveGasLineBlock = async () => {
    const manipulatedValues =
      homeUser &&
      values.accountHolder === "person" &&
      !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
      user
        ? {
            ...values,
            accountPrimaryEmail: user.email,
            accountPrimaryFirstName: user.first_name,
            accountPrimaryLastName: user.last_name,
          }
        : values;
    try {
      await preSubmissionRequestUpdate(
        manipulatedValues,
        gasServices!,
        setFormLoadValues!,
        setFormState,
        "gasLine",
        undefined,
        undefined,
        undefined,
        false,
        homeUser
      );
      setDisplayingSnackbar({
        message: "Gas block updated",
        tone: "positive",
      });
      setDisplayingAlertBanner(null);
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to save gas block",
        tone: "caution",
      });
    }
  };

  const siteConditionsError = errors?.gasLine?.siteConditions;

  const appliancesChecklistHelpText = (
    <>
      <Text>Please download and complete </Text>
      <TextLink
        href={
          "https://www.originenergy.com.au/content/dam/origin/residential/docs/elec-gas/all-appliance-checklist-act-nsw.pdf"
        }
        target="_blank"
      >
        <Strong>Natural gas appliances checklist</Strong>
      </TextLink>
      <Text>PDF form.</Text>
    </>
  );

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "gasLineBlock",
    Boolean(premiseId)
  );

  return (
    <>
      {showUpdatePremiseAlert(
        accountPayers,
        Boolean(values.accountId),
        Boolean(premiseId)
      ) ? (
        <Stack space="xlarge">
          <UpdatePremiseAlert />
        </Stack>
      ) : (
        <Form>
          <Stack
            space={{
              xs: "xxlarge",
              md: "xxxlarge",
            }}
          >
            {/* Form container */}
            <Stack
              space={{
                xs: "xlarge",
                md: "xxlarge",
              }}
            >
              {/* New or existing building */}
              {["NSW", "SA"].includes(state) && (
                <FormFieldLayout
                  text={
                    state === "SA" && values.gasLine.buildingType === "new"
                      ? "To request a service line for a new building in South Australia, call Australian Gas Networks on 1300 001 001."
                      : null
                  }
                >
                  <Field name="gasLine.buildingType">
                    {({ field }: { field: FormikProps<FieldProps> }) => (
                      <RadioGroup
                        {...field}
                        id="new-existing"
                        name="gasLine.buildingType"
                        label="New or existing building?"
                      >
                        <Box
                          display="flex"
                          flexWrap="wrap"
                          flexDirection={{
                            xs: "column",
                            sm: "row",
                          }}
                        >
                          <Box marginRight="medium">
                            <Radio
                              id="new"
                              label="New"
                              value="new"
                              onChange={(e) => {
                                handleNewGasToggle(e);
                              }}
                            />
                          </Box>
                          <Radio
                            id="existing"
                            label="Existing"
                            value="existing"
                            onChange={(e) => {
                              handleExistingGasToggle(e);
                            }}
                          />
                        </Box>
                      </RadioGroup>
                    )}
                  </Field>
                </FormFieldLayout>
              )}

              {gasInputFieldsVisible ? (
                <Stack
                  space={{
                    xs: "xlarge",
                    md: "xxlarge",
                  }}
                >
                  {!homeUser && state === "NSW" && (
                    <FormFieldLayout>
                      <ContactField
                        contactType="PLUMBER"
                        name="gasLine.licensedPlumber"
                        label="Licensed plumber"
                        validatePlumberLicense={validatePlumberLicense}
                      />
                    </FormFieldLayout>
                  )}

                  {homeUser && values.premiseAddress?.state === "NSW" && (
                    <HomeUserLicencedPlumber type="gasLine" />
                  )}

                  {homeUser && displaySiteSupervisor ? (
                    <HomeUserSiteSupervisor type="gasLine" />
                  ) : (
                    <FormFieldLayout>
                      <ContactField
                        contactType="SUPERVISOR"
                        name="siteSupervisor"
                        label="Site supervisor"
                      />
                    </FormFieldLayout>
                  )}

                  {state === "VIC" && (
                    <FormFieldLayout>
                      <DatePickerField
                        name="gasLine.siteLockupDate"
                        label="Site lock up date"
                        shouldDisableDate={isUnavailableDateForFuture}
                      />
                    </FormFieldLayout>
                  )}
                  {/* Changes to requirements */}

                  {/* Pressure */}
                  <FormFieldLayout multipleFields>
                    <Field
                      name="gasLine.pressure"
                      label="Pressure"
                      component={FormikSelectField}
                      options={pressureOptions}
                    />
                    {/* Other pressure: "0" to represent "Other" */}
                    {values.gasLine.pressure === "0" && (
                      <Field
                        component={GeneralTextField}
                        name="gasLine.otherPressure"
                        label="Other pressure value (kPa)"
                        maxLength={200}
                      />
                    )}
                  </FormFieldLayout>
                  {/* End Pressure */}
                  {/* Total load */}
                  {propertyType === "RESIDENTIAL" && (
                    <FormFieldLayout>
                      <Stack space="small">
                        <ResidentialGasLoadField
                          name="gasLine.totalLoad"
                          label="Total load, MJ/h"
                          openDialog={openDialog}
                        />
                        {hasTotalLoadDetails(
                          values.gasLine.totalLoadDetails
                        ) && (
                          <Text variant="body-small" tone="neutralLight">
                            {populateTotalLoadSummary(
                              values.gasLine.totalLoadDetails
                            )}
                          </Text>
                        )}
                      </Stack>
                    </FormFieldLayout>
                  )}

                  {/* Appliances Checklist */}
                  {propertyType === "COMMERCIAL" && (
                    <Stack space="xxlarge">
                      <FormFieldLayout
                        multipleFields
                        text={appliancesChecklistHelpText}
                      >
                        <Text>Appliances checklist</Text>
                        <Field name="gasLine.appliancesChecklistFiles">
                          {({ field }: FieldProps) => (
                            <NewPremiseFormFileUploadFormikField
                              draftId={gasServices?.gasLine?.draftId ?? ""}
                              documentTypes={[
                                DocumentTypeEnum.APPLIANCE_CHECKLIST,
                              ]}
                              field={field}
                            />
                          )}
                        </Field>
                      </FormFieldLayout>
                      <FormFieldLayout multipleFields>
                        <Field
                          component={GeneralTextField}
                          name="gasLine.totalLoad"
                          label="Total load, MJ/h"
                          maxLength={200}
                          type="number"
                        />
                      </FormFieldLayout>
                    </Stack>
                  )}
                  {/* End Appliances Checklist */}

                  {["NSW", "ACT"].includes(state) && (
                    <FormFieldLayout multipleFields>
                      <CheckboxGroup
                        id="site-conditions"
                        label="Site conditions:"
                        error={Boolean(siteConditionsError)}
                        helperText={siteConditionsError?.toString()}
                      >
                        {siteConditionsOptions.map((option) => (
                          <Field
                            type="checkbox"
                            value={option.value}
                            name="gasLine.siteConditions"
                            key={option.value}
                          >
                            {({
                              field,
                            }: {
                              field: FormikProps<FieldProps>;
                            }) => (
                              <Checkbox
                                {...field}
                                key={option.value}
                                id={option.value}
                                label={option.label}
                              />
                            )}
                          </Field>
                        ))}
                      </CheckboxGroup>
                      {values.gasLine.siteConditions.includes("other") && (
                        <Field
                          component={GeneralTextField}
                          name="gasLine.otherSiteCondition"
                          label="Other site condition"
                          maxLength={30}
                        />
                      )}
                    </FormFieldLayout>
                  )}
                  {["NSW", "ACT"].includes(state) && (
                    <FormFieldLayout multipleFields>
                      <Field
                        name="gasLine.meterLocation"
                        label="Meter location"
                        component={FormikSelectField}
                        options={meterLocationOptions}
                      />
                      {values.gasLine.meterLocation === "other" && (
                        <Field
                          component={GeneralTextField}
                          name="gasLine.otherMeterLocation"
                          label="Other meter location"
                          maxLength={200}
                        />
                      )}
                    </FormFieldLayout>
                  )}

                  {/* Site Plan */}
                  <FormFieldLayout multipleFields>
                    <Text>Site plan</Text>
                    <Field name="gasLine.sitePlanFiles">
                      {({ field }: FieldProps) => (
                        <NewPremiseFormFileUploadFormikField
                          draftId={gasServices?.gasLine?.draftId ?? ""}
                          documentTypes={[DocumentTypeEnum.SITE_PLAN]}
                          field={field}
                        />
                      )}
                    </Field>
                  </FormFieldLayout>
                  {/* End Site Plan */}
                </Stack>
              ) : null}
            </Stack>
            {/* End Form container */}
            {open && (
              <Calculator
                appliancesFromFormik={values.gasLine.totalLoadDetails}
                totalLoadFromFormik={values.gasLine.totalLoad}
                closeDialog={closeDialog}
                setFieldValue={setFieldValue}
                gasOptionName={GasOptionEnum.GAS_LINE}
              />
            )}

            {/* Actions */}
            <PermissionsActionButtons
              primaryButtonProps={{
                onHandleClick: async () =>
                  await submitBlock({
                    values,
                    setErrors,
                    validationSchema: (homeUser
                      ? formGasLineHomeUserValidationSchema(
                          isAccountReadOnly,
                          isDigitalEic
                        )
                      : formGasLineWithCompanyValidationSchema(
                          isAccountReadOnly,
                          isDigitalEic
                        )) as Yup.ObjectSchema<any>,
                    context: {
                      state,
                      propertyType,
                      gasLine: values.gasLine,
                    },
                    gasOption: "gasLine",
                    lodgeRequest,
                  }),
              }}
              secondaryButtonProps={{
                onHandleClick: saveGasLineBlock,
                disabled: gasServices?.gasLine?.autoSubmit,
              }}
            />
            {/* End Actions */}
          </Stack>
        </Form>
      )}
    </>
  );
};

export default GasLineBlock;
